import { useEffect, useState } from "react";
import { ArrowRight, FileText, IndianRupee, MessageSquareMore, Wrench } from "lucide-react";
import { Link } from "react-router-dom";

import api from "../../lib/api";
import { Loading } from "../../components/shared";
import ResidentNotificationCenter from "../../components/ResidentNotificationCenter";


const INR = (amount) =>
  `₹${Number(amount || 0).toLocaleString("en-IN", {
    maximumFractionDigits: 2,
  })}`;


function DashboardLink({ description, icon: Icon, label, testId, to, value }) {
  return (
    <Link
      className="group border border-slate-200 bg-white p-5 shadow-sm transition-[border-color,box-shadow,transform] duration-200 hover:-translate-y-1 hover:border-sky-200 hover:shadow-md"
      data-testid={testId}
      to={to}
    >
      <div className="flex items-start justify-between gap-4">
        <span className="border border-slate-100 bg-slate-50 p-2 text-sky-700"><Icon className="h-5 w-5" /></span>
        <ArrowRight className="h-4 w-4 text-slate-400 transition-transform duration-200 group-hover:translate-x-1" />
      </div>
      <p className="mt-5 text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">{label}</p>
      <p className="mt-2 font-display text-2xl font-bold text-slate-950">{value}</p>
      <p className="mt-1 text-xs leading-5 text-slate-600">{description}</p>
    </Link>
  );
}


export default function ResidentHomeModern() {
  const [dashboard, setDashboard] = useState(null);
  const [serviceCount, setServiceCount] = useState(0);
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    Promise.all([
      api.get("/resident/dashboard"),
      api.get("/resident-doorstep/services"),
      api.get("/resident-notifications"),
    ])
      .then(([dashboardResponse, serviceResponse, notificationResponse]) => {
        setDashboard(dashboardResponse.data);
        setServiceCount(serviceResponse.data.offering_count || 0);
        setUnreadCount(notificationResponse.data.unread_count || 0);
      })
      .catch(() => setDashboard({}));
  }, []);

  if (!dashboard) return <Loading />;

  const finance = dashboard.finance_summary;
  const helpdesk = dashboard.helpdesk_summary;
  const requests = dashboard.requests_summary;

  return (
    <div className="mx-auto max-w-7xl" data-testid="resident-home-page">
      <header className="border-b border-slate-200 pb-8">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-sky-700">Resident portal</p>
        <div className="mt-3 flex flex-wrap items-end justify-between gap-5">
          <div>
            <h1 className="font-display text-4xl font-bold text-slate-950 sm:text-5xl">
              Hello, {dashboard.resident_name || "Resident"}.
            </h1>
            <p className="mt-3 text-sm text-slate-600" data-testid="resident-home-society-name">
              {dashboard.society_name || "Your society"}
              {dashboard.unit_number ? ` · Unit ${dashboard.unit_number}` : ""}
            </p>
          </div>
          <Link
            className="inline-flex items-center gap-2 border border-orange-300 bg-orange-50 px-4 py-2.5 text-sm font-semibold text-orange-900 transition-colors hover:bg-orange-100"
            data-testid="resident-home-doorstep-action"
            to="/resident/doorstep-services"
          >
            <Wrench className="h-4 w-4" />
            Doorstep Services
          </Link>
        </div>
      </header>
      <section className="mt-8 grid gap-6 lg:grid-cols-12" data-testid="resident-home-bento-grid">
        <div className="space-y-6 lg:col-span-8">
          <section className="grid gap-4 sm:grid-cols-2" data-testid="resident-home-primary-actions">
            {finance?.visibility === "FULL" && (
              <DashboardLink
                description={`${finance.unpaid_bill_count || 0} unpaid bill(s)`}
                icon={IndianRupee}
                label="Maintenance outstanding"
                testId="resident-home-outstanding"
                to="/resident/finance"
                value={INR(finance.outstanding)}
              />
            )}
            {finance?.visibility === "TENANT_STATUS" && (
              <DashboardLink
                description={finance.due_date ? `Due ${finance.due_date}` : "No current bill"}
                icon={IndianRupee}
                label="Current bill status"
                testId="resident-home-tenant-finance"
                to="/resident/finance"
                value={finance.current_bill_status || "—"}
              />
            )}
            <DashboardLink
              description={serviceCount ? "Configured services ready to explore" : "No services configured yet"}
              icon={Wrench}
              label="Doorstep services"
              testId="resident-home-doorstep-services"
              to="/resident/doorstep-services"
              value={serviceCount}
            />
            {helpdesk && (
              <DashboardLink
                description={helpdesk.open_count ? "Open service requests" : "No open complaints"}
                icon={MessageSquareMore}
                label="Complaints"
                testId="resident-home-helpdesk"
                to="/resident/helpdesk"
                value={helpdesk.open_count || 0}
              />
            )}
            {requests && (
              <DashboardLink
                description={requests.pending_count ? "Requests awaiting action" : "No pending formal requests"}
                icon={FileText}
                label="Requests & NOC"
                testId="resident-home-requests"
                to="/resident/requests"
                value={requests.pending_count || 0}
              />
            )}
          </section>
          <section className="border border-slate-200 bg-slate-950 p-6 text-white" data-testid="resident-home-notice-summary">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-sky-200">Updates</p>
            <div className="mt-3 flex flex-wrap items-end justify-between gap-4">
              <div>
                <p className="font-display text-3xl font-bold">{unreadCount} unread</p>
                <p className="mt-1 text-sm text-slate-300">
                  Review your authorized notices and resident updates.
                </p>
              </div>
              <Link
                className="inline-flex items-center gap-2 border border-white/25 px-4 py-2 text-sm font-semibold transition-colors hover:bg-white/10"
                data-testid="resident-home-notices-link"
                to="/resident/notices"
              >
                View notices <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </section>
        </div>
        <div className="lg:col-span-4">
          <ResidentNotificationCenter />
        </div>
      </section>
    </div>
  );
}